import React, { type ComponentProps, type ComponentType, useContext } from 'react';

import { getAssetUrlsFromId } from '@atlassian/react-loosely-lazy-manifest';

import { getConfig, MODE } from '../../config';
import { PHASE } from '../../constants';
import { LazySuspenseContext } from '../../suspense';
import { getExport } from '../../utils';
import { createLoaderError } from '../errors';
import { type ServerLoader } from '../loader';

function load<C>(moduleId: string, loader: ServerLoader<C>) {
	try {
		return getExport(loader());
	} catch (err) {
		throw createLoaderError(err);
	}
}

export function createComponentServer<C extends ComponentType<any>>({
	dataLazyId,
	defer,
	loader,
	moduleId,
	ssr,
}: {
	dataLazyId: string;
	defer: number;
	loader: ServerLoader<C>;
	moduleId: string;
	ssr: boolean;
}) {
	return (
		props: ComponentProps<C>,
	):
		| string
		| number
		| boolean
		| Iterable<React.ReactNode>
		| React.JSX.Element
		| null
		| undefined => {
		const { fallback } = useContext(LazySuspenseContext);
		const {
			crossOrigin,
			manifest,
			onChunkRender,
			mode,
			react18,
			modernErrorFallback,
			environment,
		} = getConfig();

		const shouldLoad = ssr || environment === 'client';

		const Resolved = shouldLoad ? load(moduleId, loader) : null;

		onChunkRender(dataLazyId, moduleId, defer);

		if (mode === MODE.HYDRATE && react18) {
			if (!Resolved) {
				throw new ReactLooselyLazyNoSsrError(moduleId);
			}
			return <Resolved {...props} />;
		}

		if (modernErrorFallback) {
			if (!Resolved) {
				// lets just return the fallback without extra fuzz
				// and "error" fallback is usually totally empty, so why bother?
				return fallback;
			}
		}

		return (
			<>
				<input type="hidden" data-lazy-begin={dataLazyId} />
				{defer !== PHASE.LAZY &&
					getAssetUrlsFromId(manifest, moduleId)?.map((url: any) => (
						<link
							key={url}
							rel={defer === PHASE.PAINT ? 'preload' : 'prefetch'}
							href={url}
							crossOrigin={crossOrigin}
							as="script"
						/>
					))}
				{Resolved ? <Resolved {...props} /> : fallback}
				<input type="hidden" data-lazy-end={dataLazyId} />
			</>
		);
	};
}

class ReactLooselyLazyNoSsrError extends Error {
	skipSsr: true;
	constructor(moduleId: string) {
		super(`[no-error] React-Loosely-Lazy SSR opt out - ${moduleId}`);
		// This error is used to indicate to React that this component should not
		// render in SSR (it will instead trigger the nearest Suspense boundary)
		// so we mark it as ignored in SSR reporting.
		this.skipSsr = true;
	}
}
